/**
 * The chat verdict beside a grant row (spec 89 mock v3). Segment and chat-audience grants only: the
 * browse `audience` section stays exactly as it was, because browsing never opens chat.
 */
import type { Grant } from '../types';
import {
  CHAT_READY_TOOLTIP,
  NO_CHAT_READY_LABEL,
  NO_CHAT_READY_TOOLTIP,
  UNREACHABLE_LABEL,
  UNREACHABLE_TOOLTIP,
  alsoInChatAudienceLabel,
  alsoInChatAudienceTooltip,
  chatReadyLabel,
} from '../chatGrantCopy';

const TEAL_CHIP = 'rounded bg-teal-50 px-1.5 py-0.5 text-xs font-semibold text-teal-800';
const AMBER_CHIP = 'rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900';

function Chip({ tone, title, label }: { tone: string; title: string; label: string }) {
  return (
    <span className={tone} title={title}>
      {label}
    </span>
  );
}

function ChatAudienceChip({ grant }: { grant: Grant }) {
  if (grant.reachable === false)
    return <Chip tone={AMBER_CHIP} title={NO_CHAT_READY_TOOLTIP} label={NO_CHAT_READY_LABEL} />;
  if (grant.reachable === true && grant.segment_count != null)
    return (
      <Chip
        tone={TEAL_CHIP}
        title={CHAT_READY_TOOLTIP}
        label={chatReadyLabel(grant.segment_count)}
      />
    );
  return null;
}

function SegmentChips({ grant }: { grant: Grant }) {
  const via = grant.chat_via_audience_id;
  return (
    <>
      {via != null && (
        <Chip
          tone={TEAL_CHIP}
          title={alsoInChatAudienceTooltip(via)}
          label={alsoInChatAudienceLabel(via)}
        />
      )}
      {grant.reachable === false && (
        <Chip tone={AMBER_CHIP} title={UNREACHABLE_TOOLTIP} label={UNREACHABLE_LABEL} />
      )}
    </>
  );
}

export function GrantChatChip({ grant }: { grant: Grant }) {
  if (grant.resource_type === 'chat_audience') return <ChatAudienceChip grant={grant} />;
  if (grant.resource_type === 'segment') return <SegmentChips grant={grant} />;
  return null;
}
